import { lazy, Suspense } from "react";
import { Route, Switch, Redirect, useLocation } from "wouter";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";

// Keep the landing page in the critical route bundle. Every other page is loaded
// only when it is visited, keeping the public homepage free of CMS, Stripe, and
// form-builder code.
import Home from "./pages/Home";

const NotFound = lazy(() => import("@/pages/NotFound"));
const About = lazy(() => import("./pages/About"));
const ServiceRetail = lazy(() => import("./pages/ServiceRetail"));
const ServiceResidential = lazy(() => import("./pages/ServiceResidential"));
const ServiceCondo = lazy(() => import("./pages/ServiceCondo"));
const Properties = lazy(() => import("./pages/Properties"));
const Contact = lazy(() => import("./pages/Contact"));
const Insights = lazy(() => import("./pages/Insights"));
const InsightDetail = lazy(() => import("./pages/InsightDetail"));
const Forms = lazy(() => import("./pages/Forms"));
const FormPage = lazy(() => import("./pages/FormPage"));

const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminProperties = lazy(() => import("./pages/admin/AdminProperties"));
const AdminInquiries = lazy(() => import("./pages/admin/AdminInquiries"));
const AdminArticles = lazy(() => import("./pages/admin/AdminArticles"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminForgotPassword = lazy(() => import("./pages/admin/AdminForgotPassword"));
const AdminResetPassword = lazy(() => import("./pages/admin/AdminResetPassword"));
const AdminRegister = lazy(() => import("./pages/admin/AdminRegister"));
const AdminFormsList = lazy(() => import("./pages/admin/AdminFormsList"));
const AdminFormNew = lazy(() => import("./pages/admin/AdminFormNew"));
const AdminFormEdit = lazy(() => import("./pages/admin/AdminFormEdit"));
const AdminFormFields = lazy(() => import("./pages/admin/AdminFormFields"));
const AdminFormSubmissions = lazy(() => import("./pages/admin/AdminFormSubmissions"));
const AdminAllFormSubmissions = lazy(() => import("./pages/admin/AdminAllFormSubmissions"));
const AdminFormDropdowns = lazy(() => import("./pages/admin/AdminFormDropdowns"));
const DeferredUiProviders = lazy(() => import("./components/DeferredUiProviders"));
const ClientDataProviders = lazy(() => import("./components/ClientDataProviders"));
const AdminGuard = lazy(() => import("./components/AdminRouteGuard"));

function PageLoadingFallback() {
  return (
    <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center" aria-live="polite" aria-label="Loading page">
      <div className="w-8 h-8 border-2 border-[#E8542A] border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function Router() {
  return (
    <Suspense fallback={<PageLoadingFallback />}>
      <Switch>
        {/* Public routes */}
        <Route path="/" component={Home} />
        <Route path="/about" component={About} />
        <Route path="/services/retail" component={ServiceRetail} />
        <Route path="/services/residential" component={ServiceResidential} />
        <Route path="/services/condo" component={ServiceCondo} />
        <Route path="/properties" component={Properties} />
        <Route path="/contact" component={Contact} />
        <Route path="/insights" component={Insights} />
        <Route path="/insights/:slug" component={InsightDetail} />
        <Route path="/forms" component={Forms} />
        <Route path="/forms/:slug" component={FormPage} />

        {/* Admin routes */}
        <Route path="/admin/login" component={AdminLogin} />
        <Route path="/admin/forgot-password" component={AdminForgotPassword} />
        <Route path="/admin/reset-password" component={AdminResetPassword} />
        <Route path="/admin/register" component={AdminRegister} />
        <Route path="/admin">
          <AdminGuard><AdminDashboard /></AdminGuard>
        </Route>
        <Route path="/admin/properties">
          <AdminGuard><AdminProperties /></AdminGuard>
        </Route>
        <Route path="/admin/inquiries">
          <AdminGuard><AdminInquiries /></AdminGuard>
        </Route>
        <Route path="/admin/articles">
          <AdminGuard><AdminArticles /></AdminGuard>
        </Route>
        <Route path="/admin/users">
          <AdminGuard><AdminUsers /></AdminGuard>
        </Route>
        <Route path="/admin/settings">
          <AdminGuard><AdminSettings /></AdminGuard>
        </Route>
        <Route path="/admin/forms/new">
          <AdminGuard><AdminFormNew /></AdminGuard>
        </Route>
        <Route path="/admin/forms/dropdowns">
          <AdminGuard><AdminFormDropdowns /></AdminGuard>
        </Route>
        <Route path="/admin/forms/submissions">
          <AdminGuard><AdminAllFormSubmissions /></AdminGuard>
        </Route>
        <Route path="/admin/forms/:id/edit">
          <AdminGuard><AdminFormEdit /></AdminGuard>
        </Route>
        <Route path="/admin/forms/:id/fields">
          <AdminGuard><AdminFormFields /></AdminGuard>
        </Route>
        <Route path="/admin/forms/:id/submissions">
          <AdminGuard><AdminFormSubmissions /></AdminGuard>
        </Route>
        <Route path="/admin/forms">
          <AdminGuard><AdminFormsList /></AdminGuard>
        </Route>

        <Route path="/404" component={NotFound} />
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

function AppContent() {
  const [location] = useLocation();
  const page = <main id="main-content"><Router /></main>;

  // The homepage does not display a toast or tooltip. Deferring these Radix
  // libraries leaves the critical mobile route smaller without affecting any
  // interactive CMS or form page that does use them.
  if (location === "/") return page;
  return (
    <Suspense fallback={<PageLoadingFallback />}>
      <ClientDataProviders>
        <DeferredUiProviders>{page}</DeferredUiProviders>
      </ClientDataProviders>
    </Suspense>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="light">
        <AppContent />
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
